import { Error } from '@/components/shared/parts';
import { Chat, Order, COMPLETED_ORDER_STATUSES } from '@/types';
import { isEmpty } from '@/utils';
import { Api, ApiError } from '@/infrastructure/api';
import React, { FC, JSX, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

export type ChatFormValues = Pick<Chat, 'message'>;

const ACCEPTED_FILE_TYPES = '.jpg,.jpeg,.png,.pdf';
const MAX_FILE_SIZE_MB = 20;

export const ChatForm: FC<{
  order: Order;
  onSent: (chat: Chat) => void;
  isSubmitDisabled: boolean;
}> = (props): JSX.Element => {
  const { order, onSent, isSubmitDisabled } = props;
  const { register, handleSubmit, setValue } = useForm<ChatFormValues>({
    mode: 'onChange',
  });
  const [errors, setErrors] = useState<Array<string>>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // パートナーが論理削除なら送信ブロック
  const isDisabled = order?.recreation?.isUserDeleted;
  // 終了報告承認済みなら送信ブロック
  const isOrderCompleted = COMPLETED_ORDER_STATUSES.includes(order?.status);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0] ?? null;
    if (file && file.size >= MAX_FILE_SIZE_MB * 1024 * 1024) {
      setErrors([`${MAX_FILE_SIZE_MB}MB以上のファイルは送信できません。`]);
      e.target.value = '';
      setSelectedFile(null);
      return;
    }
    setErrors([]);
    setSelectedFile(file);
  };

  // サーバーは 422 でバリデーションの理由(文字列の配列)を返す。理由が分かるならそれを表示する
  const serverErrors = (e: unknown): Array<string> | null => {
    if (!(e instanceof ApiError) || e.status !== 422) return null;
    if (!Array.isArray(e.body)) return null;

    const messages = e.body.filter((m): m is string => typeof m === 'string');
    return isEmpty(messages) ? null : messages;
  };

  const onSubmit = async (values: ChatFormValues): Promise<void> => {
    setErrors([]);

    const message = values.message?.trim() ?? '';
    // メッセージもファイルも無いと送信できない(Chat モデルのバリデーション)ため、送信前に止める
    if (message === '' && !selectedFile) {
      setErrors(['メッセージを入力するか、ファイルを選択してください。']);
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('chat[message]', message);
      if (selectedFile) {
        formData.append('chat[file]', selectedFile);
      }

      const response = await Api.postFormData<Chat>(`orders/${order.id}/chats`, 'customer', formData);
      onSent(response.data);
      setValue('message', '');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      setErrors(
        serverErrors(e) ?? [
          'チャットの送信に失敗しました。時間をおいて再度お試しください。',
        ]
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='card-footer bg-white'>
      {!isEmpty(errors) && <Error errors={errors} />}
      {isDisabled && (
        <div className='alert alert-warning py-2 mb-2'>
          講師は削除済みのため、チャットは利用できません。
        </div>
      )}
      {isOrderCompleted && (
        <div className='alert alert-warning py-2 mb-2'>
          終了報告が承認済みのため、チャットは利用できません。
        </div>
      )}
      {!isDisabled && !isOrderCompleted && (
        <form className='chat' onSubmit={handleSubmit(onSubmit)}>
          <div className='mb-2'>
            <input
              ref={fileInputRef}
              type='file'
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleFileChange}
              className='form-control form-control-sm'
            />
            <div className='text-muted small mt-1'>
              ※ファイルサイズは{MAX_FILE_SIZE_MB}MBが上限で、PDF/JPEG/JPG/PNG形式に対応しております
            </div>
          </div>
          <div>
            <textarea
              id='chatInput'
              rows={5}
              {...register('message')}
              className='form-control chat-message'
            />
          </div>
          <div className='mt-3'>
            <button id='chatSubmit' type='submit' className='btn btn-send w-100' disabled={isSubmitting || isSubmitDisabled}>
              送信
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
